// Column sorting shared by the admin tables.
//
// Clicking a heading cycles ascending -> descending -> unsorted. The third state matters:
// returning to the order the server sent is otherwise impossible without reloading, and for
// the applications list that order is alphabetical by name, which is how the board reads
// through them.

import { useState, useCallback } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

export interface Sorting<K extends string> {
  sort: SortState<K> | null;
  /** Advances the cycle for one column. */
  toggle: (key: K) => void;
  /** Props for a heading cell, including the accessibility state. */
  headerProps: (key: K) => {
    className: string;
    onClick: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
    role: 'columnheader';
    tabIndex: 0;
    'aria-sort': 'ascending' | 'descending' | 'none';
    title: string;
  };
  /** The arrow to render beside a heading; empty when the column is not the sorted one. */
  indicator: (key: K) => string;
}

export function useSorting<K extends string>(initial: SortState<K> | null = null): Sorting<K> {
  const [sort, setSort] = useState<SortState<K> | null>(initial);

  const toggle = useCallback((key: K) => {
    setSort((current) => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;   // third click clears the sort
    });
  }, []);

  const headerProps = useCallback((key: K) => {
    const active = sort?.key === key;
    const next = !active ? 'sort ascending'
      : sort.direction === 'asc' ? 'sort descending'
      : 'remove sorting';

    return {
      className: `sortable${active ? ' sorted' : ''}`,
      onClick: () => toggle(key),
      // Headings are not buttons, so keyboard activation has to be wired up by hand.
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle(key);
        }
      },
      role: 'columnheader' as const,
      tabIndex: 0 as const,
      'aria-sort': (active
        ? (sort.direction === 'asc' ? 'ascending' : 'descending')
        : 'none') as 'ascending' | 'descending' | 'none',
      title: `Click to ${next}`,
    };
  }, [sort, toggle]);

  const indicator = useCallback(
    (key: K) => (sort?.key === key ? (sort.direction === 'asc' ? '▲' : '▼') : ''),
    [sort],
  );

  return { sort, toggle, headerProps, indicator };
}

/**
 * Sort rows by the active column, leaving them untouched when nothing is sorted.
 *
 * `values` maps a column to the value to compare. Nulls always sort last regardless of
 * direction: on this data a null means "could not be scored", and burying those under a
 * descending sort would hide exactly the rows that need a person to look at them.
 */
export function applySorting<T, K extends string>(
  rows: T[],
  sort: SortState<K> | null,
  values: Record<K, (row: T) => string | number | null | undefined>,
): T[] {
  if (!sort) return rows;

  const read = values[sort.key];
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = read(a);
    const right = read(b);

    const leftMissing = left === null || left === undefined || left === '';
    const rightMissing = right === null || right === undefined || right === '';
    if (leftMissing && rightMissing) return 0;
    if (leftMissing) return 1;
    if (rightMissing) return -1;

    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * factor;
    }
    // localeCompare so names with accents sort where a reader expects -- the data is full
    // of them, and a byte comparison would file Suárez after Szabo.
    return String(left).localeCompare(String(right), undefined, { numeric: true }) * factor;
  });
}
